import { useState } from 'react';
import { Alert, Box, Button, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import { login } from './api';

export function LoginPage({
  onLoggedIn,
}: {
  onLoggedIn: (mustChangePassword: boolean) => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { mustChangePassword } = await login(username, password);
      onLoggedIn(mustChangePassword);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'login failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, width: 380 }} elevation={2}>
        {/* The full lockup here, the bare symbol in the app bar: this is the
            one screen with room for it, and it is the screen that has to look
            like us before anyone has logged in. */}
        <Box
          component="img"
          src="/dikonek-logo.png"
          alt="Dikonek"
          sx={{ width: 132, height: 'auto', display: 'block', mx: 'auto', mb: 3 }}
        />
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Dikonek Tunnel
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Fleet remote access. The broker relays ciphertext only.
        </Typography>

        <form onSubmit={submit}>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy || !username || !password}
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </Stack>
        </form>

        {/* A fresh box being set up lands here first. Installing the agent needs
            no account, so point at the public install page rather than letting
            the login screen look like a dead end. */}
        <Typography variant="body2" color="text.secondary" mt={3}>
          Setting up this machine? <Link href="/download">Install the agent</Link> — no sign-in
          needed.
        </Typography>
      </Paper>
    </Box>
  );
}
